// ENG-7891 / ADR-0049 slice 3 — the dark id-keyed migration engine (manager side).
//
// Converts an EXISTING legacy codename-keyed agent host dir to the agent_id-keyed
// layout at the pre-spawn tick, so the agent keeps a rename-stable directory key
// and (crucially) its Claude Code transcript store. Ships DARK: gated per-agent by
// the `id-keyed-layout-migration` registry flag (default false), never armed until
// a test-host rehearsal passes (the last ENG-7891 acceptance gate).
//
// Where it runs (ADR-0049 §4): from `ensurePersistentSession`, at the top of the
// not-running (`!isSessionHealthy`) funnel, BEFORE the spawn cwd is computed and
// BEFORE the funnel's own `provisionIsolationHook(codeName, agent_id)` reprovision.
// That placement delivers three of the four preconditions for free:
//   1. Session-down quiesce: the agent's in-session channel MCP writers
//      (Slack/Telegram/Teams/direct-chat) are children of the Claude Code session,
//      so they are already dead when we run. The ONLY external writer that
//      survives a dead session is the host-resident WhatsApp inbound API route —
//      handled explicitly by the skip-if-active precondition below.
//   2. Hook freshness: the funnel unconditionally regenerates the id-aware
//      isolation hook (which follows the codename symlink to the id project dir)
//      after this returns and before spawn, every tick — so a migrated agent can
//      never spawn under a stale codename-only deny hook.
//   3. Layout coherence: because we move the dir + create the symlink before the
//      spawn cwd is computed, `getProjectDir(codeName)` resolves through the fresh
//      symlink to the id-keyed path, so every downstream consumer is id-keyed.
//
// The filesystem swap itself (two atomic renames with a pre-staged symlink) lives
// in `migrateAgentDirToIdKeyed` in @augmented/core, beside the layout contract.
// This module owns the policy: the flag gate, the WhatsApp-writer quiesce check,
// and the transcript-store move.

import { existsSync, lstatSync, readlinkSync, renameSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import { migrateAgentDirToIdKeyed } from '@augmented/core/provisioning/frameworks/claudecode/index.js';
import { sessionTranscriptDir } from './daily-session.js';
import type { HostFlagStore } from './feature-flags-host.js';

/** The registry flag (ADR-0022) that arms an INITIAL migration, per-agent. */
export const ID_KEYED_MIGRATION_FLAG = 'id-keyed-layout-migration';

export type IdKeyedMigrationOutcome =
  | 'migrated' // legacy real dir converted to id-keyed this call
  | 'recovered' // a prior interrupted migration was completed forward this call
  | 'already-id-keyed' // codename already a symlink -> id dir (no-op)
  | 'no-agent' // nothing on disk for this agent
  | 'skipped-flag-off' // legacy layout, but the per-agent flag isn't armed
  | 'skipped-external-writer' // legacy layout, but an active WhatsApp writer blocks a safe move
  | 'conflict' // both a real codename dir and a real id dir exist — operator required
  | 'failed'; // an fs error was thrown; layout left as-is, retried next tick

export interface IdKeyedMigrationDeps {
  /** The host flag store — only the per-agent boolean accessor is used. */
  flagStore: Pick<HostFlagStore, 'getBooleanForAgent'>;
  /** `/host/refresh` channel_configs, used to detect an active WhatsApp binding
   * (the one external writer a dead session does not quiesce). Null when the
   * agent has no channels. */
  channelConfigs: Record<string, { config: unknown; status: string }> | null;
  log: (msg: string) => void;
  /** Override for tests; defaults to the process home dir. */
  home?: string;
}

/**
 * The one external writer that is NOT a child of the (dead) agent session: the
 * host-resident WhatsApp inbound API route, which `mkdir -p`s
 * `~/.augmented/{codeName}/whatsapp-pending-inbound/` on an inbound webhook. If
 * it fired during the microsecond window between the two renames it would
 * re-create the codename path as a real dir, corrupting the layout. So an agent
 * with any WhatsApp binding is deferred to the manual rename runbook rather than
 * auto-migrated. Fail-closed: treat a configured binding in ANY status as active,
 * and also honour an on-disk pending-inbound dir (an inbound was received) even
 * if the config lookup is momentarily empty.
 */
function agentHasActiveWhatsapp(
  channelConfigs: IdKeyedMigrationDeps['channelConfigs'],
  codeNameDir: string,
): boolean {
  if (channelConfigs && Object.prototype.hasOwnProperty.call(channelConfigs, 'whatsapp')) {
    return true;
  }
  try {
    if (existsSync(join(codeNameDir, 'whatsapp-pending-inbound'))) return true;
  } catch {
    // stat error — treat as absent.
  }
  return false;
}

/**
 * Move the Claude Code transcript store from the legacy codename-cwd key to the
 * id-cwd key. Idempotent and safe to call on every tick for an id-keyed agent:
 * a no-op once the source is gone. Called AFTER the dir/symlink move (dir-first
 * ordering), so completing a crash that died between the dir move and this step
 * is flag-independent. Never clobbers an existing destination.
 */
function finishTranscriptMove(
  oldCwd: string,
  newCwd: string,
  codeName: string,
  log: (msg: string) => void,
): void {
  const from = sessionTranscriptDir(oldCwd);
  const to = sessionTranscriptDir(newCwd);
  if (from === to) return;
  let fromExists = false;
  let toExists = false;
  try {
    fromExists = existsSync(from);
  } catch {
    /* stat error */
  }
  if (!fromExists) return; // already moved, or the agent never wrote a transcript
  try {
    toExists = existsSync(to);
  } catch {
    /* stat error */
  }
  if (toExists) {
    // The id-keyed transcript dir already exists alongside the legacy one — do
    // not merge/clobber. Leave the legacy dir for an operator to reconcile.
    log(
      `[id-keyed-migration] transcript store for '${codeName}' already present at the id-keyed key; ` +
        `leaving the legacy dir in place for an operator to reconcile`,
    );
    return;
  }
  renameSync(from, to);
  log(`[id-keyed-migration] moved transcript store for '${codeName}' to the id-keyed key`);
}

/**
 * Attempt a dark, once-per-agent id-keyed migration for one agent at the
 * pre-spawn tick. Best-effort and non-throwing: any fs error is caught, logged,
 * and returned as `'failed'` so the spawn path is never blocked (the migration
 * is retried on the next tick). Recovery of an interrupted prior migration is
 * flag-INDEPENDENT (a half-moved agent is broken and must be completed); only
 * INITIATING a fresh conversion is gated by the per-agent flag.
 */
export function maybeMigrateAgentToIdKeyedLayout(
  agent: { agent_id: string; code_name: string },
  deps: IdKeyedMigrationDeps,
): IdKeyedMigrationOutcome {
  const home = deps.home ?? homedir();
  const { code_name: codeName, agent_id: agentId } = agent;
  const codeNamePath = join(home, '.augmented', codeName); // agent-dir-allow: migration source path (ADR-0049 slice 3)
  const idPath = join(home, '.augmented', agentId);
  // The LITERAL codename/id cwd strings — the transcript store is keyed by the
  // flattened cwd, so the OLD key uses the codename path (the agent's cwd before
  // the symlink existed) and the NEW key uses the id path.
  const oldCwd = join(home, '.augmented', codeName, 'project'); // agent-dir-allow: legacy transcript-key cwd (ADR-0049 slice 3)
  const newCwd = join(idPath, 'project');

  let codeNameKind: 'symlink' | 'realdir' | 'absent';
  try {
    codeNameKind = lstatSync(codeNamePath).isSymbolicLink() ? 'symlink' : 'realdir';
  } catch {
    codeNameKind = 'absent';
  }
  const idExists = existsSync(idPath);

  try {
    // Already migrated. Dir-first ordering means a crash could have died between
    // the dir move and the transcript move, so finish the transcript (idempotent).
    if (codeNameKind === 'symlink') {
      // Fail closed, same posture as migrateAgentDirToIdKeyed / ensureIdKeyedLayout:
      // a codename symlink pointing at a DIFFERENT agent means the transcript keyed
      // on this codename does not belong at THIS agent's id key. Never relocate
      // another agent's history — surface it for an operator instead.
      const target = readlinkSync(codeNamePath);
      if (target !== agentId) {
        deps.log(
          `[id-keyed-migration] CONFLICT for '${codeName}': codename symlink points at "${target}", ` +
            `expected "${agentId}"; refusing to touch the transcript store (operator reconciliation required)`,
        );
        return 'conflict';
      }
      finishTranscriptMove(oldCwd, newCwd, codeName, deps.log);
      return 'already-id-keyed';
    }

    // Crash recovery: the dir move committed (id dir present) but the codename
    // symlink was never created. Complete it forward, flag-INDEPENDENT — a
    // half-moved agent has no codename entry and is otherwise unreachable.
    if (codeNameKind === 'absent' && idExists) {
      migrateAgentDirToIdKeyed(codeName, agentId, { home });
      finishTranscriptMove(oldCwd, newCwd, codeName, deps.log);
      deps.log(
        `[id-keyed-migration] recovered an interrupted migration for '${codeName}' -> ${agentId}`,
      );
      return 'recovered';
    }

    // Nothing on disk (a fresh agent is created id-keyed by the provisioner).
    if (codeNameKind === 'absent') return 'no-agent';

    // codeNameKind === 'realdir': a legacy agent. INITIATING a migration is the
    // flag-gated, precondition-guarded path.
    if (!deps.flagStore.getBooleanForAgent(ID_KEYED_MIGRATION_FLAG, agentId)) {
      return 'skipped-flag-off';
    }
    if (idExists) {
      deps.log(
        `[id-keyed-migration] CONFLICT for '${codeName}': a real codename dir AND a real ${agentId} ` +
          `dir both exist; refusing to migrate (operator reconciliation required)`,
      );
      return 'conflict';
    }
    if (agentHasActiveWhatsapp(deps.channelConfigs, codeNamePath)) {
      deps.log(
        `[id-keyed-migration] deferred '${codeName}': active WhatsApp channel — the host-resident ` +
          `inbound writer cannot be quiesced from the manager; use the manual rename runbook`,
      );
      return 'skipped-external-writer';
    }

    // Commit: dir + symlink (core), then the transcript store.
    const status = migrateAgentDirToIdKeyed(codeName, agentId, { home });
    if (status === 'conflict') {
      // An id dir raced into existence between our check and the core call.
      deps.log(
        `[id-keyed-migration] CONFLICT for '${codeName}': ${agentId} dir appeared mid-migration; ` +
          `refusing to migrate (operator reconciliation required)`,
      );
      return 'conflict';
    }
    finishTranscriptMove(oldCwd, newCwd, codeName, deps.log);
    deps.log(
      `[id-keyed-migration] migrated '${codeName}' to the id-keyed layout (~/.augmented/${agentId}); ` +
        `codename is now a compatibility symlink`,
    );
    return 'migrated';
  } catch (err) {
    deps.log(
      `[id-keyed-migration] FAILED for '${codeName}': ${(err as Error).message}; ` +
        `leaving the current layout in place, will retry next tick`,
    );
    return 'failed';
  }
}
